import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { ENV } from '../../config/environment';
import { API_CONFIG } from '../../config/api';
import { PlatformErrorHandler } from '../utils/platform-error-handler';

export interface PlatformClientConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  headers: Record<string, string>;
}

export interface PlatformRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  cache?: boolean;
  cacheTTL?: number;
}

export interface PlatformResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  meta?: {
    requestId: string;
    timestamp: string;
    version: string;
  };
}

export class PlatformClient {
  private instance: AxiosInstance;
  private errorHandler: PlatformErrorHandler;

  constructor() {
    this.errorHandler = new PlatformErrorHandler();
    this.instance = axios.create({
      baseURL: ENV.API_BASE_URL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
        ...(API_CONFIG.headers || {}),
      },
    });
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.instance.interceptors.request.use(
      (config) => {
        // Ensure token is always sent
        const token = this.getToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        } else {
          // Debug: Log if token is missing for problematic endpoints
          if (config.url && config.url.includes('/pulse')) {
            console.warn('[PlatformClient] No token found for Pulse endpoint:', config.url);
          }
        }
        
        const workspaceId = this.getWorkspaceId();
        if (workspaceId) {
          config.headers['X-Workspace-ID'] = workspaceId;
        }
        
        const orgId = this.getOrganizationId();
        if (orgId) {
          config.headers['X-Organization-ID'] = orgId;
        }
        
        config.headers['X-Request-ID'] = this.generateRequestId();
        
        // Debug logging for POST requests
        if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
          console.debug('[PlatformClient]', config.method.toUpperCase(), config.url, {
            hasAuth: !!config.headers['Authorization'],
            hasWorkspace: !!workspaceId,
            hasOrg: !!orgId,
          });
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(this.errorHandler.handleRequestError(error));
      },
    );

    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        // Handle 401 Unauthorized - clear token and reload
        if (error.response?.status === 401) {
          console.error('[PlatformClient] Unauthorized - clearing auth and reloading');
          try {
            localStorage.clear();
            window.location.href = '/login';
          } catch (e) {
            console.error('Failed to clear localStorage:', e);
          }
        }
        return Promise.reject(this.errorHandler.handleResponseError(error));
      },
    );
  }

  private getToken(): string | null {
    try {
      const token = localStorage.getItem('cyzor_access_token');
      if (!token) {
        // Try legacy key
        return localStorage.getItem('cyzor_auth_token');
      }
      return token;
    } catch {
      console.error('[PlatformClient] Error reading token from localStorage');
      return null;
    }
  }

  private getWorkspaceId(): string | null {
    try {
      return localStorage.getItem('cyzor_active_workspace_id');
    } catch {
      return null;
    }
  }

  private getOrganizationId(): string | null {
    try {
      return localStorage.getItem('cyzor_organization_id');
    } catch {
      return null;
    }
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async get(url: string, config?: PlatformRequestConfig): Promise<any> {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  async post(url: string, data?: any, config?: PlatformRequestConfig): Promise<any> {
    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  async put(url: string, data?: any, config?: PlatformRequestConfig): Promise<any> {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  async patch(url: string, data?: any, config?: PlatformRequestConfig): Promise<any> {
    const response = await this.instance.patch(url, data, config);
    return response.data;
  }

  async delete(url: string, config?: PlatformRequestConfig): Promise<any> {
    const response = await this.instance.delete(url, config);
    return response.data;
  }

  getAxiosInstance(): AxiosInstance {
    return this.instance;
  }
}

export const platformClient = new PlatformClient();